import {
  Smartphone, Zap, RefreshCw, ShieldCheck,
  Droplets, Sparkles, CreditCard, Users, Truck, BarChart3,
} from "lucide-react";
import whiteHero from "@/assets/products/white-hero.webp";
import whiteFeatures from "@/assets/products/white-features.webp";
import whiteTeam from "@/assets/products/white-team.webp";
import whiteProfile from "@/assets/products/white-profile.webp";
import type { ProductPageDict } from "@/lib/shop/product-dict";

const SLUG = "white-pvc";
const SKU = "BC-PVCW";
const BASE_PRICE = 54;
const PRODUCT_URL_EN = "https://banana.cards/shop/white-pvc";
const PRODUCT_URL_VI = "https://banana.cards/vi/shop/white-pvc";
const RATING = { value: "4.78", count: "214" };

const BUNDLES = [
  { qty: 1,  label: "1 card",  perCard: 1.0 },
  { qty: 3,  label: "3 cards", perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards", perCard: 0.82, save: 18, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.72, save: 28, badge: "Best value" },
] as const;

const BUNDLES_VI = [
  { qty: 1,  label: "1 thẻ",  perCard: 1.0 },
  { qty: 3,  label: "3 thẻ",  perCard: 0.9,  save: 10, badge: "Tiết kiệm 10%" },
  { qty: 5,  label: "5 thẻ",  perCard: 0.82, save: 18, badge: "Phổ biến nhất" },
  { qty: 10, label: "10 thẻ", perCard: 0.72, save: 28, badge: "Giá trị nhất" },
] as const;

const GALLERY = [
  { src: whiteHero,     alt: "Custom White PVC AI business card front and back with NFC and QR" },
  { src: whiteFeatures, alt: "White PVC card features — printed logo, NFC chip, QR code" },
  { src: whiteProfile,  alt: "Customizable digital profiles on iPhone" },
  { src: whiteTeam,     alt: "Team & enterprise control dashboard" },
];

const GALLERY_VI = [
  { src: whiteHero,     alt: "Thẻ PVC trắng AI mặt trước và sau với NFC và mã QR" },
  { src: whiteFeatures, alt: "Cấu tạo thẻ PVC trắng — logo in, chip NFC, mã QR" },
  { src: whiteProfile,  alt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone" },
  { src: whiteTeam,     alt: "Bảng điều khiển đội nhóm và doanh nghiệp" },
];

export const whitePvcDict: ProductPageDict = {
  slug: SLUG,
  routePath: "/shop/white-pvc",
  productUrl: PRODUCT_URL_EN,
  basePrice: BASE_PRICE,
  sku: SKU,
  rating: RATING,
  seo: {
    title: "Custom White PVC AI Business Card — banana.cards",
    description: "Crisp white PVC NFC business card with full-color print, embedded NFC chip and QR code. AI-powered profile sharing and CRM built in.",
    ogTitle: "Custom White PVC AI Business Card",
    ogDescription: "Premium white PVC NFC card. Tap once, share instantly, capture every contact — AI follow-ups built in.",
  },
  jsonLdDescription:
    "The Custom White PVC AI Business Card combines a crisp white PVC body with full-color printing, an embedded NFC chip and a printed QR code linked to your banana.cards profile.",
  eyebrow: "banana.cards · White PVC",
  productName: "Custom White PVC AI Business Card",
  productIntro:
    "Clean, modern and built for every industry. Crisp white PVC with full-color printing on both sides, an embedded NFC chip and a printed QR code — a single card that handles introductions, capture and follow-up automatically.",
  ratingLabel: "4.78 · 214 reviews",
  perCardSuffix: "/ card · Free worldwide shipping over $50",
  saveBadge: (pct) => `Save ${pct}%`,
  bundles: BUNDLES,
  bundleSection: {
    eyebrow: "Bundle & save",
    heading: { pre: "Equip your team in", em: "crisp white PVC." },
    sub: "Each card carries its own profile — perfect for co-founders, sales teams or whole companies.",
    bundleLabel: "Bundle & save",
    perCardLabel: "per card",
    selected: "Selected",
    choose: "Choose",
  },
  usps: [
    { icon: Smartphone, label: "Share your details with one tap" },
    { icon: Zap,        label: "Collect their details back with one click" },
    { icon: RefreshCw,  label: "No app needed — works on both Apple & Android" },
    { icon: ShieldCheck,label: "Lifetime hardware guarantee" },
  ],
  features: {
    eyebrow: "Why white PVC",
    heading: { pre: "Clean canvas.", em: "Smart card." },
    items: [
      { icon: CreditCard, title: "Full-color printing",       desc: "Edge-to-edge print on both sides — logos, photos, gradients and brand colors land sharp and saturated." },
      { icon: Sparkles,   title: "AI follow-ups built-in",    desc: "Every tap lands in your banana CRM, enriched and ready for AI-drafted follow-ups." },
      { icon: Droplets,   title: "Water & scratch resistant", desc: "Premium PVC core handles spills, sticky fingers and daily wallet life without fading." },
      { icon: Users,      title: "Team-friendly rollout",     desc: "Bulk orders for whole teams — branded, fulfilled and billed in one go." },
      { icon: Truck,      title: "Free worldwide shipping",   desc: "Orders over $50 ship free to 100+ countries — 3-5 business days." },
      { icon: BarChart3,  title: "Real-time analytics",       desc: "See every tap, click and lead in your dashboard — know which events actually convert." },
    ],
  },
  anatomy: {
    eyebrow: "Card anatomy",
    heading: { pre: "Every detail,", em: "engineered." },
    image: whiteFeatures,
    imageAlt: "Anatomy of the White PVC AI business card — printed logo, NFC chip and QR code",
    items: [
      { t: "Full-color front & back",  d: "Photo-quality edge-to-edge print on premium white PVC — your brand at its sharpest." },
      { t: "Durable white PVC core",   d: "Water-resistant, scratch-resistant — built to live in wallets and pockets for years." },
      { t: "Embedded NFC chip",        d: "Tap to share your full profile to any modern iPhone or Android — no app required." },
      { t: "Printed QR code",          d: "A perfect fallback for older devices or thicker phone cases — every introduction lands." },
    ],
  },
  profile: {
    eyebrow: "Your profile, your way",
    heading: { pre: "One card.", em: "Infinite identities." },
    body: "Pick a layout, add your colors, photo, links, services and CTAs — and switch between work, side-hustle or recruiting profiles from the same PVC card.",
    bullets: [
      "12+ designer-made layouts & themes",
      "Add video, gallery, services, FAQs",
      "Multi-language profiles",
      "Lead-capture form on every card",
    ],
    image: whiteProfile,
    imageAlt: "Customisable digital profiles on iPhone",
  },
  compare: {
    eyebrow: "Paper vs PVC",
    heading: { pre: "Paper cards get lost.", em: "PVC gets remembered." },
    sub: "You deserve something that actually works — and that people actually keep.",
    paperLabel: "Paper business cards",
    productLabel: "Custom White PVC AI Business Card",
    paper: [
      "Hand out paper cards and hope they keep it",
      "Manually follow up days later",
      "Forget who you met or what you said",
      "No analytics, no CRM, no visibility",
      "You look like everyone else",
    ],
    product: [
      "Tap once, your details save instantly",
      "Automatic smart follow-ups",
      "Every contact captured automatically",
      "Real-time analytics show which events convert",
      "Modern, clean, unforgettable",
    ],
  },
  steps: {
    eyebrow: "How it works",
    heading: { pre: "You don't need to explain.", em: "You just tap." },
    sub: "They get everything that matters — instantly.",
    stepLabel: (n) => `Step ${n}`,
    items: [
      { title: "Tap once to share easily",   desc: "Tap your NFC Banana Card on any smartphone, no app required. Your profile, links and calendar open instantly." },
      { title: "They save you instantly",    desc: "They add you straight to their contacts — your info stays where they actually look: their phone." },
      { title: "You get their details back", desc: "Each new lead lands automatically in your Banana dashboard and syncs to your CRM — no manual entry." },
    ],
  },
  team: {
    eyebrow: "For teams",
    heading: { pre: "Built for", em: "teams that close." },
    body: "Roll out branded white PVC cards across your whole team, manage every profile from one dashboard, and route every new contact straight into your shared CRM.",
    cta: "Order for your team",
    image: whiteTeam,
    imageAlt: "Team and enterprise control dashboard",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "FAQ",
    heading: { pre: "Frequently asked", em: "questions." },
    items: [
      { q: "What's different about the White PVC card vs paper?", a: "It's a permanent, water-resistant card with full-color edge-to-edge print, an embedded NFC chip and a printed QR — it lasts years, gets remembered and triggers automated AI follow-ups." },
      { q: "How do I customize my white PVC card?", a: "After checkout we send you a short design brief. Upload your logo, pick brand colors, choose front/back artwork — our team mocks it up, you approve, we produce." },
      { q: "Does the card work through phone cases?", a: "Yes. The embedded NFC antenna reads through most silicone, leather and plastic cases. The printed QR is a perfect fallback for thick or metal-backed cases." },
      { q: "What digital features come with the card?", a: "Every card includes a banana.cards profile: unlimited links, multi-language profiles, lead capture form, Apple & Google Wallet pass, real-time analytics and CRM sync." },
      { q: "Is the printing scratch and water resistant?", a: "Yes. We use premium PVC printing with a protective laminate — colors stay sharp through daily wallet life, spills and wear." },
      { q: "Can I use the card with my existing CRM?", a: "Yes. Banana syncs natively with HubSpot, Salesforce, Pipedrive, Notion, Google Contacts and 50+ tools via Zapier." },
      { q: "Do I need batteries or to charge the card?", a: "No. The NFC chip is passive — it draws a tiny amount of power from the smartphone reading it. Nothing to charge, ever." },
      { q: "Can I track how many times my card is used?", a: "Yes. Your dashboard shows every tap and QR scan in real time — including location, device and which links got clicked." },
      { q: "Is there a digital backup if I don't have my card?", a: "Yes. Your profile lives at a unique URL — share it via link, QR, email signature or Apple/Google Wallet pass." },
      { q: "How long does production take?", a: "Most cards ship within 5–7 business days of design approval." },
    ],
  },
  outro: {
    heading: { pre: "Network with", em: "your brand front and center." },
    body: "Order before 3pm and we ship the next business day. Free worldwide shipping over $50.",
    primaryCta: "Get my White PVC card",
    secondaryCta: "See all products",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Add to cart" },
  gallery: GALLERY,
};

export const whitePvcDictVi: ProductPageDict = {
  ...whitePvcDict,
  routePath: "/vi/shop/white-pvc",
  productUrl: PRODUCT_URL_VI,
  seo: {
    title: "Thẻ Danh Thiếp PVC Trắng AI Tùy Chỉnh — banana.cards",
    description: "Thẻ NFC PVC trắng in màu đầy đủ, chip NFC tích hợp và mã QR. Chia sẻ hồ sơ bằng AI và CRM tích hợp sẵn.",
    ogTitle: "Thẻ Danh Thiếp PVC Trắng AI Tùy Chỉnh",
    ogDescription: "Thẻ NFC PVC trắng cao cấp. Chạm một lần, chia sẻ tức thì, thu thập mọi liên hệ — tích hợp AI theo dõi tự động.",
    locale: "vi_VN",
  },
  jsonLdDescription:
    "Thẻ Danh Thiếp PVC Trắng AI Tùy Chỉnh kết hợp thân PVC trắng tinh khiết với in màu đầy đủ, chip NFC tích hợp và mã QR liên kết với hồ sơ banana.cards.",
  eyebrow: "banana.cards · PVC trắng",
  productName: "Thẻ Danh Thiếp PVC Trắng AI Tùy Chỉnh",
  productIntro:
    "Sạch sẽ, hiện đại và phù hợp mọi ngành nghề. PVC trắng tinh với in màu đầy đủ hai mặt, chip NFC tích hợp và mã QR in — một thẻ duy nhất xử lý giới thiệu, thu thập và theo dõi tự động.",
  ratingLabel: "4,78 · 214 đánh giá",
  perCardSuffix: "/ thẻ · Miễn phí vận chuyển toàn cầu cho đơn trên $50",
  saveBadge: (pct) => `Tiết kiệm ${pct}%`,
  bundles: BUNDLES_VI,
  bundleSection: {
    eyebrow: "Mua combo, tiết kiệm",
    heading: { pre: "Trang bị cho đội bằng", em: "PVC trắng tinh khiết." },
    sub: "Mỗi thẻ có hồ sơ riêng — hoàn hảo cho đồng sáng lập, đội bán hàng hoặc cả công ty.",
    bundleLabel: "Mua combo, tiết kiệm",
    perCardLabel: "mỗi thẻ",
    selected: "Đã chọn",
    choose: "Chọn",
  },
  usps: [
    { icon: Smartphone, label: "Chia sẻ thông tin chỉ với một cú chạm" },
    { icon: Zap,        label: "Thu lại thông tin của họ chỉ với một cú nhấp" },
    { icon: RefreshCw,  label: "Không cần ứng dụng — hoạt động trên cả iPhone và Android" },
    { icon: ShieldCheck,label: "Bảo hành phần cứng trọn đời" },
  ],
  features: {
    eyebrow: "Vì sao chọn PVC trắng",
    heading: { pre: "Khung trắng tinh.", em: "Thẻ thông minh." },
    items: [
      { icon: CreditCard, title: "In màu toàn bộ",            desc: "In tràn cạnh hai mặt — logo, ảnh, gradient và màu thương hiệu sắc nét, đậm nét." },
      { icon: Sparkles,   title: "AI theo dõi tích hợp",       desc: "Mỗi cú chạm đều được lưu vào CRM banana, làm giàu dữ liệu và sẵn sàng cho AI soạn lời theo dõi." },
      { icon: Droplets,   title: "Chống nước & chống xước",   desc: "Lõi PVC cao cấp chịu được nước, dấu tay và sử dụng trong ví hàng ngày mà không phai." },
      { icon: Users,      title: "Triển khai dễ cho đội",     desc: "Đặt số lượng lớn cho cả đội — gắn thương hiệu, giao hàng và thanh toán cùng một lần." },
      { icon: Truck,      title: "Vận chuyển toàn cầu miễn phí", desc: "Đơn trên $50 được miễn phí giao đến hơn 100 quốc gia — 3-5 ngày làm việc." },
      { icon: BarChart3,  title: "Phân tích thời gian thực",   desc: "Xem mọi cú chạm, lượt nhấp và khách tiềm năng trên bảng điều khiển." },
    ],
  },
  anatomy: {
    eyebrow: "Cấu tạo thẻ",
    heading: { pre: "Mọi chi tiết,", em: "đều được tinh chỉnh." },
    image: whiteFeatures,
    imageAlt: "Cấu tạo Thẻ PVC trắng AI — logo in, chip NFC và mã QR",
    items: [
      { t: "In màu toàn bộ hai mặt", d: "In chất lượng ảnh tràn cạnh trên PVC trắng cao cấp — thương hiệu của bạn sắc nét nhất." },
      { t: "Lõi PVC trắng bền bỉ",   d: "Chống nước, chống xước — bền bỉ trong ví và túi nhiều năm." },
      { t: "Chip NFC tích hợp",      d: "Chạm để chia sẻ toàn bộ hồ sơ với mọi iPhone hoặc Android hiện đại — không cần ứng dụng." },
      { t: "Mã QR in sẵn",           d: "Phương án dự phòng hoàn hảo cho thiết bị cũ hoặc ốp lưng dày." },
    ],
  },
  profile: {
    eyebrow: "Hồ sơ của bạn, theo cách của bạn",
    heading: { pre: "Một thẻ.", em: "Vô vàn bản dạng." },
    body: "Chọn bố cục, thêm màu sắc, ảnh, liên kết, dịch vụ và CTA — và chuyển đổi giữa hồ sơ công việc, dự án phụ hoặc tuyển dụng từ cùng một thẻ PVC.",
    bullets: [
      "12+ bố cục & chủ đề thiết kế sẵn",
      "Thêm video, thư viện, dịch vụ, FAQ",
      "Hồ sơ đa ngôn ngữ",
      "Biểu mẫu thu khách tiềm năng trên mọi thẻ",
    ],
    image: whiteProfile,
    imageAlt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone",
  },
  compare: {
    eyebrow: "Giấy vs PVC",
    heading: { pre: "Thẻ giấy bị thất lạc.", em: "PVC được ghi nhớ." },
    sub: "Bạn xứng đáng có thứ thực sự hoạt động — và người ta thực sự giữ lại.",
    paperLabel: "Thẻ danh thiếp giấy",
    productLabel: "Thẻ Danh Thiếp PVC Trắng AI Tùy Chỉnh",
    paper: [
      "Phát thẻ giấy và hy vọng họ giữ",
      "Theo dõi thủ công vài ngày sau",
      "Quên mất bạn đã gặp ai và nói gì",
      "Không phân tích, không CRM, không tầm nhìn",
      "Bạn trông giống mọi người khác",
    ],
    product: [
      "Chạm một lần, thông tin được lưu tức thì",
      "Theo dõi thông minh tự động",
      "Mọi liên hệ được thu thập tự động",
      "Phân tích thời gian thực cho biết sự kiện nào chuyển đổi",
      "Hiện đại, sạch sẽ, khó quên",
    ],
  },
  steps: {
    eyebrow: "Cách hoạt động",
    heading: { pre: "Bạn không cần giải thích.", em: "Bạn chỉ cần chạm." },
    sub: "Họ nhận được mọi thứ quan trọng — ngay lập tức.",
    stepLabel: (n) => `Bước ${n}`,
    items: [
      { title: "Chạm một lần để chia sẻ",  desc: "Chạm thẻ Banana NFC vào bất kỳ điện thoại nào, không cần ứng dụng." },
      { title: "Họ lưu bạn tức thì",       desc: "Họ thêm bạn vào danh bạ — thông tin nằm đúng nơi họ thực sự nhìn." },
      { title: "Bạn nhận lại thông tin",   desc: "Mỗi khách tiềm năng tự động vào bảng điều khiển Banana và đồng bộ CRM." },
    ],
  },
  team: {
    eyebrow: "Dành cho đội nhóm",
    heading: { pre: "Xây cho", em: "đội bán hàng chốt sale." },
    body: "Triển khai thẻ PVC trắng gắn thương hiệu cho cả đội, quản lý mọi hồ sơ trên một bảng điều khiển, định tuyến mọi liên hệ mới vào CRM chung.",
    cta: "Đặt cho đội của bạn",
    image: whiteTeam,
    imageAlt: "Bảng điều khiển đội và doanh nghiệp",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "Câu hỏi thường gặp",
    heading: { pre: "Câu hỏi", em: "thường gặp." },
    items: [
      { q: "Thẻ PVC trắng khác thẻ giấy ở điểm nào?", a: "Đây là thẻ bền vĩnh viễn, chống nước, in màu tràn cạnh hai mặt, có chip NFC và mã QR — bền nhiều năm, được nhớ và kích hoạt theo dõi AI tự động." },
      { q: "Làm sao tùy chỉnh thẻ PVC trắng?", a: "Sau khi đặt hàng, chúng tôi gửi bản brief thiết kế ngắn. Tải logo, chọn màu thương hiệu, mặt trước/sau — đội thiết kế sẽ làm mockup, bạn duyệt, chúng tôi sản xuất." },
      { q: "Thẻ có hoạt động qua ốp điện thoại không?", a: "Có. Ăng-ten NFC tích hợp đọc qua hầu hết ốp silicone, da và nhựa. Mã QR in là phương án dự phòng cho ốp dày hoặc mặt kim loại." },
      { q: "Thẻ có tính năng số gì?", a: "Mỗi thẻ bao gồm hồ sơ banana.cards: liên kết không giới hạn, hồ sơ đa ngôn ngữ, biểu mẫu thu khách, thẻ Apple & Google Wallet, phân tích thời gian thực và đồng bộ CRM." },
      { q: "In có chống xước và chống nước không?", a: "Có. Chúng tôi dùng in PVC cao cấp với lớp cán bảo vệ — màu sắc giữ sắc nét qua sử dụng hàng ngày." },
      { q: "Có thể dùng với CRM hiện tại không?", a: "Có. Banana đồng bộ tự nhiên với HubSpot, Salesforce, Pipedrive, Notion, Google Contacts và 50+ công cụ qua Zapier." },
      { q: "Có cần pin hoặc sạc thẻ không?", a: "Không. Chip NFC là thụ động — lấy điện nhỏ từ điện thoại đọc nó. Không pin, không sạc." },
      { q: "Có thể theo dõi thẻ được sử dụng bao nhiêu lần?", a: "Có. Bảng điều khiển hiển thị mọi cú chạm và quét QR theo thời gian thực." },
      { q: "Có bản sao lưu kỹ thuật số nếu mất thẻ không?", a: "Có. Hồ sơ của bạn nằm tại một URL duy nhất — chia sẻ qua liên kết, QR, chữ ký email hoặc thẻ Apple/Google Wallet." },
      { q: "Sản xuất mất bao lâu?", a: "Hầu hết thẻ được giao trong 5-7 ngày làm việc kể từ khi duyệt thiết kế." },
    ],
  },
  outro: {
    heading: { pre: "Kết nối với", em: "thương hiệu của bạn ở vị trí trung tâm." },
    body: "Đặt trước 3 giờ chiều, chúng tôi giao vào ngày làm việc kế tiếp. Miễn phí vận chuyển toàn cầu cho đơn trên $50.",
    primaryCta: "Mua thẻ PVC trắng",
    secondaryCta: "Xem tất cả sản phẩm",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Thêm vào giỏ" },
  gallery: GALLERY_VI,
};

const PRODUCT_URL_EL = "https://banana.cards/el/shop/white-pvc";
const BUNDLES_EL = [
  { qty: 1, label: "1 κάρτα", perCard: 1.0 },
  { qty: 3, label: "3 κάρτες", perCard: 0.9, save: 10, badge: "Κέρδος 10%" },
  { qty: 5, label: "5 κάρτες", perCard: 0.82, save: 18, badge: "Δημοφιλέστερο" },
  { qty: 10, label: "10 κάρτες", perCard: 0.72, save: 28, badge: "Καλύτερη τιμή" },
] as const;
const GALLERY_EL = [
    { src: whiteHero, alt: "Ψηφιακή επαγγελματική κάρτα AI από λευκό PVC — μπροστινή και πίσω όψη με NFC και QR" },
    { src: whiteFeatures, alt: "Χαρακτηριστικά κάρτας από λευκό PVC — λογότυπο, NFC και QR" },
    { src: whiteProfile, alt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone" },
    { src: whiteTeam, alt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις" }
  ];
export const whitePvcDictEl: ProductPageDict = {
  ...whitePvcDict,
  routePath: "/el/shop/white-pvc",
  perCardSuffix: "/ κάρτα · Δωρεάν αποστολή παγκοσμίως για αγορές άνω των $50",
  bundleSection: { eyebrow: "Πακέτα με όφελος", heading: { pre: "Εξοπλίστε την ομάδα σας με κάρτες από", em: "κατάλευκο PVC." }, sub: "Κάθε κάρτα έχει το δικό της προφίλ — ιδανική για συνιδρυτές, ομάδες πωλήσεων ή ολόκληρες εταιρείες.", bundleLabel: "Πακέτα με όφελος", perCardLabel: "ανά κάρτα", selected: "Επιλεγμένο", choose: "Επιλέξτε" },
  usps: [
    { icon: Smartphone, label: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα" },
    { icon: Zap, label: "Λάβετε και τα δικά τους στοιχεία με ένα κλικ" },
    { icon: RefreshCw, label: "Χωρίς εφαρμογή — σε iPhone και Android" },
    { icon: ShieldCheck, label: "Εγγύηση εφ’ όρου ζωής για την κάρτα" }
  ],
  steps: { eyebrow: "Πώς λειτουργεί", heading: { pre: "Δεν χρειάζονται εξηγήσεις.", em: "Μόνο ένα άγγιγμα." }, sub: "Όλες οι σημαντικές πληροφορίες φτάνουν στα χέρια τους — αμέσως.", stepLabel: (n) => `Βήμα ${n}`, items: [
    { title: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα", desc: "Αγγίζετε με την κάρτα NFC της Banana ένα smartphone, χωρίς να χρειάζεται εφαρμογή. Το προφίλ, οι σύνδεσμοι και το ημερολόγιό σας ανοίγουν αμέσως." },
    { title: "Σας αποθηκεύουν αμέσως στις επαφές", desc: "Σας προσθέτουν απευθείας στις επαφές τους — τα στοιχεία σας μένουν εκεί που τα αναζητούν: στο κινητό τους." },
    { title: "Λαμβάνετε και τα δικά τους στοιχεία", desc: "Κάθε νέα επαφή υποψήφιου πελάτη καταχωρίζεται αυτόματα στον πίνακα ελέγχου Banana και συγχρονίζεται με το CRM σας — χωρίς χειροκίνητη εισαγωγή." }
  ] },
  buyBox: { addToCart: "Προσθήκη στο καλάθι" },
  productUrl: PRODUCT_URL_EL,
  seo: { title: "Λευκή κάρτα PVC με AI — banana.cards", description: "Λευκή επαγγελματική κάρτα PVC με έγχρωμη εκτύπωση, NFC και QR. Μοιραστείτε το προφίλ σας με AI και ενσωματωμένο CRM.", ogTitle: "Εξατομικευμένη λευκή επαγγελματική κάρτα PVC με AI", ogDescription: "Λευκή κάρτα NFC από PVC υψηλής ποιότητας. Ένα άγγιγμα για άμεση κοινοποίηση και καταχώριση κάθε επαφής. Ενσωματωμένη επικοινωνία με AI.", locale: "el_GR" },
  jsonLdDescription: "Η εξατομικευμένη ψηφιακή επαγγελματική κάρτα AI από λευκό PVC συνδυάζει λευκό PVC με έγχρωμη εκτύπωση, ενσωματωμένο chip NFC και τυπωμένο κωδικό QR που οδηγούν στο προφίλ σας στο banana.cards.",
  eyebrow: "banana.cards · Λευκό PVC",
  productName: "Ψηφιακή επαγγελματική κάρτα AI από λευκό PVC",
  productIntro: "Λιτή, σύγχρονη και ιδανική για κάθε κλάδο. Κατάλευκο PVC με έγχρωμη εκτύπωση και στις δύο όψεις, ενσωματωμένο chip NFC και τυπωμένο κωδικό QR. Μία κάρτα αναλαμβάνει αυτόματα τη γνωριμία, τη συλλογή στοιχείων και την επικοινωνία που ακολουθεί.",
  ratingLabel: "4,78 · 214 αξιολογήσεις",
  saveBadge: (pct) => `Κέρδος ${pct}%`,
  bundles: BUNDLES_EL,
  features: { eyebrow: "Γιατί να επιλέξετε λευκό PVC", heading: { pre: "Λιτή εμφάνιση.", em: "Έξυπνη κάρτα." }, items: [
    { icon: CreditCard, title: "Έγχρωμη εκτύπωση", desc: "Εκτύπωση μέχρι τις άκρες και στις δύο όψεις — λογότυπα, φωτογραφίες, διαβαθμίσεις και εταιρικά χρώματα με ευκρίνεια και ζωντάνια." },
    { icon: Sparkles, title: "Ενσωματωμένη επικοινωνία με AI", desc: "Κάθε άγγιγμα καταχωρίζεται στο CRM της Banana, με εμπλουτισμένα στοιχεία επαφής, έτοιμα για μηνύματα συνέχειας που συντάσσει το AI." },
    { icon: Droplets, title: "Αντοχή στο νερό και στις γρατζουνιές", desc: "Το PVC υψηλής ποιότητας αντέχει σε πιτσιλιές, δαχτυλιές και καθημερινή χρήση στο πορτοφόλι, χωρίς να ξεθωριάζει." },
    { icon: Users, title: "Εύκολη οργάνωση για όλη την ομάδα", desc: "Μαζικές παραγγελίες για ολόκληρες ομάδες — με την εταιρική σας ταυτότητα, ενιαία αποστολή και τιμολόγηση." },
    { icon: Truck, title: "Δωρεάν αποστολή παγκοσμίως", desc: "Δωρεάν αποστολή για αγορές άνω των $50 σε 100+ χώρες — σε 3–5 εργάσιμες ημέρες." },
    { icon: BarChart3, title: "Αναλυτικά στοιχεία σε πραγματικό χρόνο", desc: "Δείτε κάθε άγγιγμα, κλικ και υποψήφιο πελάτη στον πίνακα ελέγχου σας — και μάθετε ποιες εκδηλώσεις φέρνουν πραγματικά πελάτες." }
  ] },
  anatomy: { eyebrow: "Η κατασκευή της κάρτας", heading: { pre: "Κάθε λεπτομέρεια,", em: "μελετημένη." }, image: whiteFeatures, imageAlt: "Ψηφιακή επαγγελματική κάρτα AI από λευκό PVC — τυπωμένο λογότυπο, chip NFC και κωδικός QR", items: [
    { t: "Έγχρωμη εκτύπωση και στις δύο όψεις", d: "Εκτύπωση φωτογραφικής ποιότητας μέχρι τις άκρες σε λευκό PVC υψηλής ποιότητας — για να αναδεικνύεται η εταιρική σας ταυτότητα." },
    { t: "Ανθεκτικό λευκό PVC", d: "Αντοχή στο νερό και στις γρατζουνιές — σχεδιασμένη για χρόνια χρήσης σε πορτοφόλια και τσέπες." },
    { t: "Ενσωματωμένο chip NFC", d: "Με ένα άγγιγμα μοιράζεστε ολόκληρο το προφίλ σας με κάθε σύγχρονο iPhone ή Android — χωρίς εφαρμογή." },
    { t: "Τυπωμένος κωδικός QR", d: "Η ιδανική εναλλακτική για παλαιότερες συσκευές ή πιο χοντρές θήκες κινητού — για να μη χάνετε καμία γνωριμία." }
  ] },
  profile: { eyebrow: "Το προφίλ σας, όπως το θέλετε", heading: { pre: "Μία κάρτα.", em: "Αμέτρητες ταυτότητες." }, bullets: [
    "12+ διατάξεις και θέματα από επαγγελματίες σχεδιαστές",
    "Προσθέστε βίντεο, συλλογή εικόνων, υπηρεσίες και συχνές ερωτήσεις",
    "Προφίλ σε πολλές γλώσσες",
    "Φόρμα συλλογής στοιχείων υποψήφιων πελατών σε κάθε κάρτα"
  ], imageAlt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone", body: "Επιλέξτε διάταξη, προσθέστε χρώματα, φωτογραφία, συνδέσμους, υπηρεσίες και κουμπιά δράσης. Εναλλάξτε προφίλ για τη δουλειά, τα παράλληλα έργα ή τις προσλήψεις σας, από την ίδια κάρτα PVC.", image: whiteProfile },
  compare: { eyebrow: "Χαρτί ή PVC;", heading: { pre: "Οι χάρτινες κάρτες χάνονται.", em: "Το PVC μένει αξέχαστο." }, sub: "Αξίζετε κάτι που αποδίδει πραγματικά — και που οι άλλοι θέλουν να κρατήσουν.", paperLabel: "Χάρτινες επαγγελματικές κάρτες", productLabel: "Ψηφιακή επαγγελματική κάρτα AI από λευκό PVC", paper: [
    "Μοιράζετε χάρτινες κάρτες και ελπίζετε να τις κρατήσουν",
    "Επικοινωνείτε ξανά χειροκίνητα, μέρες αργότερα",
    "Ξεχνάτε ποιον γνωρίσατε ή τι συζητήσατε",
    "Χωρίς αναλυτικά στοιχεία, CRM ή εικόνα των αποτελεσμάτων",
    "Δεν ξεχωρίζετε από τους υπόλοιπους"
  ], product: [
    "Ένα άγγιγμα αρκεί για άμεση αποθήκευση των στοιχείων σας",
    "Έξυπνη, αυτοματοποιημένη επικοινωνία μετά τη γνωριμία",
    "Αυτόματη καταχώριση κάθε επαφής",
    "Αναλυτικά στοιχεία σε πραγματικό χρόνο δείχνουν ποιες εκδηλώσεις φέρνουν πελάτες",
    "Σύγχρονη, λιτή, αξέχαστη"
  ] },
  team: { eyebrow: "Για ομάδες", heading: { pre: "Σχεδιασμένη για", em: "ομάδες που κλείνουν συμφωνίες." }, body: "Εξοπλίστε όλη την ομάδα σας με κάρτες από λευκό PVC με την εταιρική σας ταυτότητα. Διαχειριστείτε κάθε προφίλ από έναν πίνακα ελέγχου και στείλτε κάθε νέα επαφή απευθείας στο κοινό σας CRM.", cta: "Παραγγείλετε για την ομάδα σας", image: whiteTeam, imageAlt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις", teamsHref: "/teams" },
  faq: { eyebrow: "Συχνές ερωτήσεις", heading: { pre: "Οι πιο συχνές", em: "ερωτήσεις σας." }, items: [
    { q: "Σε τι διαφέρει η λευκή κάρτα PVC από τη χάρτινη;", a: "Είναι μια ανθεκτική κάρτα που χρησιμοποιείτε ξανά και ξανά, με αντοχή στο νερό, έγχρωμη εκτύπωση μέχρι τις άκρες, ενσωματωμένο chip NFC και τυπωμένο QR. Διαρκεί χρόνια, σας κάνει αξέχαστους και ενεργοποιεί αυτοματοποιημένα μηνύματα συνέχειας με AI." },
    { q: "Πώς προσαρμόζω τη λευκή κάρτα PVC μου;", a: "Μετά την παραγγελία, σας στέλνουμε μια σύντομη φόρμα για το σχέδιο. Ανεβάζετε το λογότυπό σας, επιλέγετε τα εταιρικά χρώματα και τα γραφικά για τις δύο όψεις. Η ομάδα μας ετοιμάζει την προεπισκόπηση και, μόλις την εγκρίνετε, ξεκινάμε την παραγωγή." },
    { q: "Λειτουργεί η κάρτα μέσα από θήκες κινητών;", a: "Ναι. Η ενσωματωμένη κεραία NFC λειτουργεί μέσα από τις περισσότερες θήκες σιλικόνης, δέρματος και πλαστικού. Για χοντρές θήκες ή θήκες με μεταλλική πλάτη, υπάρχει ο τυπωμένος κωδικός QR." },
    { q: "Ποιες ψηφιακές λειτουργίες περιλαμβάνει η κάρτα;", a: "Κάθε κάρτα περιλαμβάνει προφίλ banana.cards: απεριόριστους συνδέσμους, προφίλ σε πολλές γλώσσες, φόρμα συλλογής στοιχείων υποψήφιων πελατών, κάρτα για Apple Wallet και Google Wallet, αναλυτικά στοιχεία σε πραγματικό χρόνο και συγχρονισμό με CRM." },
    { q: "Αντέχει η εκτύπωση στις γρατζουνιές και στο νερό;", a: "Ναι. Χρησιμοποιούμε εκτύπωση υψηλής ποιότητας σε PVC με προστατευτική πλαστικοποίηση — τα χρώματα παραμένουν ζωντανά παρά την καθημερινή χρήση στο πορτοφόλι, τις πιτσιλιές και τη φθορά." },
    { q: "Μπορώ να χρησιμοποιήσω την κάρτα με το υπάρχον CRM μου;", a: "Ναι. Η Banana συγχρονίζεται απευθείας με HubSpot, Salesforce, Pipedrive, Notion, Google Contacts και με 50+ εργαλεία μέσω Zapier." },
    { q: "Χρειάζεται η κάρτα μπαταρίες ή φόρτιση;", a: "Όχι. Το chip NFC είναι παθητικό και αντλεί ελάχιστη ενέργεια από το smartphone που το διαβάζει. Δεν χρειάζεται ποτέ φόρτιση." },
    { q: "Μπορώ να δω πόσες φορές χρησιμοποιείται η κάρτα μου;", a: "Ναι. Ο πίνακας ελέγχου εμφανίζει κάθε άγγιγμα και σάρωση QR σε πραγματικό χρόνο, μαζί με την τοποθεσία, τη συσκευή και τους συνδέσμους που επιλέχθηκαν." },
    { q: "Υπάρχει ψηφιακή εναλλακτική όταν δεν έχω την κάρτα μαζί μου;", a: "Ναι. Το προφίλ σας έχει μια μοναδική διεύθυνση URL. Μοιραστείτε το με σύνδεσμο, QR, υπογραφή email ή κάρτα στο Apple Wallet και το Google Wallet." },
    { q: "Πόσο διαρκεί η παραγωγή;", a: "Οι περισσότερες κάρτες αποστέλλονται εντός 5–7 εργάσιμων ημερών από την έγκριση του σχεδίου." }
  ] },
  outro: { heading: { pre: "Δικτυωθείτε με", em: "την εταιρική σας ταυτότητα στο επίκεντρο." }, body: "Παραγγείλετε πριν από τις 15:00 και αποστέλλουμε την επόμενη εργάσιμη ημέρα. Δωρεάν αποστολή παγκοσμίως για αγορές άνω των $50.", primaryCta: "Αποκτήστε την κάρτα σας από λευκό PVC", secondaryCta: "Δείτε όλα τα προϊόντα", shopHref: "/shop" },
  gallery: GALLERY_EL
};
